import {
  createContext,
  Dispatch,
  MutableRefObject,
  ReactNode,
  SetStateAction,
  useContext,
  useRef,
  useState,
} from 'react';
import {
  BrushZoomConfig,
  SelectedDateLine,
  VixCombinedChartData,
  VixConfig,
} from 'types';
import { DEFAULT_BRUSH_ZOOM_CONFIG } from 'config';
import { useTheme } from '@mui/material/styles';
import { getNextAvailableColor } from 'util/iVol';
import { getQueryDate, prevBusinessDayOpenMarket } from 'util/shared';

interface VixContextValue {
  config: VixConfig;
  updateConfig: (patch: Partial<VixConfig>) => void;

  selectedDates: SelectedDateLine[];
  setSelectedDates: Dispatch<SetStateAction<SelectedDateLine[]>>;

  visibleChartLines: string[];
  setVisibleChartLines: Dispatch<SetStateAction<string[]>>;

  zoomConfig: BrushZoomConfig;
  setZoomConfig: Dispatch<SetStateAction<BrushZoomConfig>>;

  chartData: VixCombinedChartData[];
  setChartData: Dispatch<SetStateAction<VixCombinedChartData[]>>;

  loading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  error: string | null;
  setError: Dispatch<SetStateAction<string | null>>;

  chartRef: MutableRefObject<HTMLDivElement | null>;
}

const VixContext = createContext<VixContextValue | null>(null);

interface VixProviderProps {
  config: VixConfig;
  onConfigChange: (patch: Partial<VixConfig>) => void;
  children: ReactNode;
}

export const VixProvider = ({
  config,
  onConfigChange,
  children,
}: VixProviderProps) => {
  const theme = useTheme();
  const availableColors = Object.values(theme.palette.iVol.termStructure);

  // Initialize selected dates: today + prevBusinessDay (default) + any persisted additional dates
  const [selectedDates, setSelectedDates] = useState<SelectedDateLine[]>(() => {
    const today = getQueryDate(true);
    const prevMarketDay = prevBusinessDayOpenMarket(today);
    const usedColors: string[] = [];

    const todayColor = availableColors[0] ?? theme.palette.primary.main;
    usedColors.push(todayColor);

    const prevDayColor = availableColors[1] ?? theme.palette.primary.main;
    usedColors.push(prevDayColor);

    const defaultDates: SelectedDateLine[] = [
      {
        key: today.format('YYYY-MM-DD'),
        tradeDate: today,
        color: todayColor,
      },
      {
        key: prevMarketDay.format('YYYY-MM-DD'),
        tradeDate: prevMarketDay,
        color: prevDayColor,
      },
    ];

    // Append persisted additional dates from config
    const additionalDates = config.additionalDates.map((entry) => {
      const color =
        entry.color && entry.color !== ''
          ? entry.color
          : (getNextAvailableColor(usedColors, availableColors) ??
            theme.palette.primary.main);
      usedColors.push(color);
      return {
        key: entry.key,
        tradeDate: entry.tradeDate,
        color,
        notDisplayed: entry.notDisplayed,
      };
    });

    return [...defaultDates, ...additionalDates];
  });

  // Initialize visible chart lines: all date keys minus any with notDisplayed
  const [visibleChartLines, setVisibleChartLines] = useState<string[]>(() => {
    const today = getQueryDate(true);
    const prevMarketDay = prevBusinessDayOpenMarket(today);
    const allKeys = [
      today.format('YYYY-MM-DD'),
      prevMarketDay.format('YYYY-MM-DD'),
    ];
    for (const entry of config.additionalDates) {
      if (!(entry.notDisplayed ?? false)) {
        allKeys.push(entry.key);
      }
    }
    return allKeys;
  });

  const [zoomConfig, setZoomConfig] = useState<BrushZoomConfig>(
    DEFAULT_BRUSH_ZOOM_CONFIG,
  );
  const [chartData, setChartData] = useState<VixCombinedChartData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const chartRef = useRef<HTMLDivElement | null>(null);

  return (
    <VixContext.Provider
      value={{
        config,
        updateConfig: onConfigChange,
        selectedDates,
        setSelectedDates,
        visibleChartLines,
        setVisibleChartLines,
        zoomConfig,
        setZoomConfig,
        chartData,
        setChartData,
        loading,
        setLoading,
        error,
        setError,
        chartRef,
      }}
    >
      {children}
    </VixContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useVixCtx = (): VixContextValue => {
  const ctx = useContext(VixContext);
  if (ctx == null) {
    throw new Error('useVixCtx must be used within a VixProvider');
  }
  return ctx;
};
